import {
  KNOWLEDGE_FILE_TOO_LARGE_I18N_KEY,
  KNOWLEDGE_MAX_CONCURRENT_UPLOADS,
} from "@frontend-shared/constants";
import {
  classifyKnowledgeUploadError,
  isKnowledgeFileOverSizeLimit,
} from "@frontend-shared/utils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAppConfig } from "@/api/app-config/useAppConfig";
import { getProjectUuid } from "@/lib/auth";

import type { IKnowledgeUploadGuards } from "./useKnowledgeUploadGuards.types";

// NOTE: NSAI-7817 — what a knowledge upload checks and says, for both the
// Knowledge page and a chat folder's knowledge panel:
//   - at most KNOWLEDGE_MAX_CONCURRENT_UPLOADS files of a batch upload at once;
//   - a file over the backend's published cap is refused before it is sent;
//   - an oversized (413) or dropped (no response) upload gets a sentence the
//     user can act on, instead of multer's English "File too large" or nothing;
//   - a queued file is dropped, not sent, if the project changed meanwhile —
//     the getter is the one the api client reads for X-Project-Uuid.
// Anything else keeps the server's own message (e.g. a 409 duplicate).
export const useKnowledgeUploadGuards = (): IKnowledgeUploadGuards => {
  // NOTE: The instance's `t` — the keys carry their namespace ("knowledge:…",
  // "common:…"), which the hook's namespace-bound `t` does not accept.
  const { i18n } = useTranslation("knowledge");
  const { data: appConfig } = useAppConfig();
  const maxFileSizeMb = appConfig?.knowledgeMaxFileSizeMb;

  return useMemo(
    () => ({
      maxConcurrentUploads: KNOWLEDGE_MAX_CONCURRENT_UPLOADS,
      validate: (file) =>
        isKnowledgeFileOverSizeLimit({
          fileSizeBytes: file.size,
          maxFileSizeMb,
        })
          ? i18n.t(KNOWLEDGE_FILE_TOO_LARGE_I18N_KEY, {
              fileName: file.name,
              limit: maxFileSizeMb,
            })
          : null,
      describeError: ({ params: file, error }) => {
        const message = classifyKnowledgeUploadError({
          error,
          fileSizeBytes: file.size,
          maxFileSizeMb,
        });
        return message
          ? i18n.t(message.i18nKey, {
              ...message.i18nParams,
              fileName: file.name,
            })
          : null;
      },
      getScopeKey: getProjectUuid,
    }),
    [i18n, maxFileSizeMb],
  );
};
